export const SUPPORT_SESSION_LOCKED_STATUS = 423

export type AgentSupportLockRetry = () => Promise<unknown>

// Counts refusals rather than holding a boolean: the dialog has to tell a
// retry that came back locked from one that got through, and a boolean that is
// already true cannot report the second refusal.
let refusals = 0
let retry: AgentSupportLockRetry | null = null
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((listener) => listener())

export const subscribeToAgentSupportLock = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getAgentSupportLockSnapshot = () => refusals

export const isAgentSupportLocked = () => refusals > 0

export const reportAgentSupportLock = () => {
  refusals += 1
  notify()
}

export const clearAgentSupportLock = () => {
  if (refusals === 0 && retry === null) return

  refusals = 0
  retry = null
  notify()
}

// The wrapper that sees the 423 knows nothing about the step the employee was
// on, so the submit handler hands back a way to run itself again. Replaying the
// bare request instead would save the answers without advancing the flow.
export const offerAgentSupportLockRetry = (handler: AgentSupportLockRetry) => {
  const had = retry !== null
  retry = handler
  if (!had) notify()
}

export const getAgentSupportLockRetry = () => retry

export const hasAgentSupportLockRetry = () => retry !== null

export const isAgentSupportLockError = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  (error as { status?: unknown }).status === SUPPORT_SESSION_LOCKED_STATUS

// A locked write is refused for as long as the session lives, so React Query's
// immediate auto-retry can only collect a second refusal. This says nothing
// about the employee choosing to try again a minute later.
export const retryUnlessAgentSupportLock = (
  failureCount: number,
  error: Error
) => failureCount < 1 && !isAgentSupportLockError(error)
